import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Observable } from 'rxjs';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { LDFlagValue } from 'launchdarkly-js-client-sdk';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.accessibility-enabled]': 'enableDataTableAccessibilityValue'
  }
})
export class SliderComponent implements OnInit, OnChanges, OnDestroy{
  @Input() min: number=0;
  @Input() max: number=3;
  @Input() minSelectedValue: number=0;
  @Input() maxSelectedValue: number=0;
  @Input() step: number = 1; // Step size
  @Input() label: string = 'Range';
  @Output() sliderValueChanges = new EventEmitter();
  minValue=0;
  maxValue=0;
  minInputId: string = '';
  maxInputId: string = '';
  descriptionId: string = '';
  
  // Feature flag for accessibility
  enableDataTableAccessibility$!: Observable<LDFlagValue>;
  enableDataTableAccessibilityValue: boolean = false;
  private subsink: SubSink = new SubSink();

  constructor(private readonly ldService: LaunchDarklyService) {}

  ngOnInit(): void {
    // Initialize feature flag observable
    this.enableDataTableAccessibility$ = this.ldService.getFlagValue(
      FEATURE_FLAGS.ENABLE_DATA_TABLE_ACCESSIBILITY,
      false
    );
    
    // Subscribe to get synchronous value for logic
    this.subsink.add(
      this.enableDataTableAccessibility$.subscribe((value) => {
        this.enableDataTableAccessibilityValue = !!value;
      })
    );

    // Generate unique IDs for accessibility - only when flag is ON
    if (this.enableDataTableAccessibilityValue) {
      const uniqueId = Math.random().toString(36).substring(2, 11);
      this.minInputId = `slider-min-${uniqueId}`;
      this.maxInputId = `slider-max-${uniqueId}`;
      this.descriptionId = `slider-description-${uniqueId}`;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(this.minSelectedValue){
      this.minValue = this.minSelectedValue;
    }
    if(this.maxSelectedValue){
      this.maxValue = this.maxSelectedValue
    }
  }


  updateMinThumbPosition() {
    if (this.minValue >= this.maxValue) {
      this.minValue = this.maxValue - this.step;
    }

    this.sliderValueChanges.emit({minvalue:this.minValue,maxvalue:this.maxValue});

  }

  updateMaxThumbPosition() {
    if (this.maxValue <= this.minValue) {
      this.maxValue = this.minValue + this.step;
    }

    this.sliderValueChanges.emit({minvalue:this.minValue,maxvalue:this.maxValue});
  }

  get leftPosition(): string {
    const percentage = ((this.minValue - this.min) / (this.max - this.min)) * 100;

    return `${Math.max(0, Math.min(percentage, 100))}%`;
  }

  get rightPosition(): string {
    const percentage = ((this.maxValue - this.min) / (this.max - this.min)) * 100;

    return `${Math.max(0, Math.min(100 - percentage, 100))}%`;
  }

  get minAccessibleLabel(): string {
    // Only provide accessible label when feature flag is ON
    if (this.enableDataTableAccessibilityValue) {
      return `${this.label} minimum value`;
    }
    return '';
  }

  get maxAccessibleLabel(): string {
    // Only provide accessible label when feature flag is ON
    if (this.enableDataTableAccessibilityValue) {
      return `${this.label} maximum value`;
    }
    return '';
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }


}
